import type { AuraMetrics } from "./useAura";

const FOCUS_COLOR: Record<AuraMetrics["focus"], string> = {
  Calm: "#2DD4BF",
  Alert: "#F59E0B",
  Drowsy: "#EF4444",
};

export default function AuraRadar({ m, size = 240 }: { m: AuraMetrics; size?: number }) {
  const c = size / 2;
  const color = FOCUS_COLOR[m.focus];
  // metric arms: perclos, mar, yaw normalised 0..1
  const arms = [
    { label: "PERCLOS", v: Math.min(1, m.perclos / 35), ang: -90 },
    { label: "YAWN", v: Math.min(1, m.mar / 0.5), ang: 30 },
    { label: "YAW", v: Math.min(1, Math.abs(m.yaw) / 30), ang: 150 },
  ];

  return (
    <div className="relative mx-auto aspect-square w-full" style={{ maxWidth: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full">
        {/* concentric rings */}
        {[0.3, 0.55, 0.8, 1].map((r, i) => (
          <circle key={i} cx={c} cy={c} r={c * r * 0.9} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth={1} />
        ))}
        {/* sweeping radar arm */}
        <line
          x1={c}
          y1={c}
          x2={c}
          y2={c - c * 0.9}
          stroke={color}
          strokeWidth={1.5}
          opacity={0.5}
          style={{ transformOrigin: `${c}px ${c}px`, animation: "spin 4s linear infinite" }}
        />
        {/* metric arms */}
        {arms.map((a) => {
          const rad = (a.ang * Math.PI) / 180;
          const len = c * 0.85 * a.v;
          const x = c + Math.cos(rad) * len;
          const y = c + Math.sin(rad) * len;
          const lx = c + Math.cos(rad) * c * 0.95;
          const ly = c + Math.sin(rad) * c * 0.95;
          return (
            <g key={a.label}>
              <line x1={c} y1={c} x2={x} y2={y} stroke={color} strokeWidth={2.5} strokeLinecap="round" />
              <circle cx={x} cy={y} r={3} fill={color} />
              <text x={lx} y={ly} fontSize={8} fill="#64748B" fontFamily="monospace" textAnchor="middle">
                {a.label}
              </text>
            </g>
          );
        })}
        {/* core */}
        <circle cx={c} cy={c} r={c * 0.26} fill={`${color}22`} stroke={color} strokeWidth={1.5} />
        <text x={c} y={c - 2} fontSize={15} fill={color} fontWeight="bold" textAnchor="middle" fontFamily="Inter">
          {m.focus}
        </text>
        <text x={c} y={c + 14} fontSize={9} fill="#64748B" textAnchor="middle" fontFamily="monospace">
          FOCUS STATE
        </text>
      </svg>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
